import { useState, useEffect, useMemo } from 'react';
import { api } from '../../../services/api';
import CardGrid from '../componentes/CardGrid';
import EditModal from '../componentes/EditModal';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import SearchBar from '../componentes/SearchBar';
import { getImageUrl } from '../utils/imageUrl';
import { Plus, Cake, MapPin, User, QrCode } from 'lucide-react';

const calcularEdad = (fechaNac) => {
  if (!fechaNac || fechaNac === '0000-00-00') return 'Desconocida';
  const nac = new Date(fechaNac);
  if (isNaN(nac.getTime())) return 'Desconocida';
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  if (edad < 1) {
    const meses = m < 0 ? m + 12 : m;
    return meses <= 0 ? 'Recién nacido' : `${meses} mes${meses !== 1 ? 'es' : ''}`;
  }
  return `${edad} año${edad !== 1 ? 's' : ''}`;
};

const SexoFilter = ({ value, onChange }) => {
  const opciones = [
    { value: 'todos', label: 'Todos' },
    { value: '0', label: '♂ Machos' },
    { value: '1', label: '♀ Hembras' },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.4rem' }}>
      {opciones.map(op => {
        const active = value === op.value;
        return (
          <button
            key={op.value}
            onClick={() => onChange(op.value)}
            style={{
              padding: '0.5rem 0.9rem', borderRadius: 999, fontSize: '0.85rem', fontWeight: 700,
              border: active ? '2px solid var(--sp-primary)' : '1px solid #ddd',
              background: active ? 'var(--sp-primary-soft, #eee7ff)' : '#fff',
              color: active ? 'var(--sp-primary)' : '#666',
              cursor: 'pointer',
            }}
          >
            {op.label}
          </button>
        );
      })}
    </div>
  );
};

const MascotasPanel = () => {
  const [mascotas, setMascotas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [search, setSearch] = useState('');
  const [sexoFiltro, setSexoFiltro] = useState('todos');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [resMascotas, resUsuarios, resCodigos] = await Promise.all([
        api.getMascotas(),
        api.getUsuarios(),
        api.getCodigos(),
      ]);
      setMascotas(resMascotas.data?.data || resMascotas.data || []);
      setUsuarios(resUsuarios.data?.data || resUsuarios.data || []);
      setCodigos(resCodigos.data?.data || resCodigos.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const usuariosMap = useMemo(
    () => Object.fromEntries(usuarios.map(u => [String(u.id), `${u.nombre} ${u.apellido}`])),
    [usuarios]
  );
  const codigosMap = useMemo(
    () => Object.fromEntries(codigos.map(c => [String(c.id), c.codigo_unico])),
    [codigos]
  );

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mascotas.filter(m => {
      if (sexoFiltro !== 'todos' && String(m.sexo) !== sexoFiltro) return false;
      if (!q) return true;
      const dueño = usuariosMap[String(m.id_usuario)] || '';
      const codigo = codigosMap[String(m.codigo_id)] || '';
      return (
        m.nombre?.toLowerCase().includes(q) ||
        String(m.id).includes(q) ||
        dueño.toLowerCase().includes(q) ||
        codigo.toLowerCase().includes(q)
      );
    });
  }, [mascotas, search, sexoFiltro, usuariosMap, codigosMap]);

  const stats = useMemo(() => ({
    total: mascotas.length,
    machos: mascotas.filter(m => m.sexo === 0).length,
    hembras: mascotas.filter(m => m.sexo === 1).length,
  }), [mascotas]);

  // Al editar, mapeamos urlImg a imagen para el modal
  const handleEdit = (m) => {
    setSelected({ ...m, imagen: m.urlImg });
    setImageFile(null);
    setShowEdit(true);
  };

  const handleCreate = () => { setSelected(null); setImageFile(null); setShowEdit(true); };
  const handleDelete = (id) => { setSelected(mascotas.find(m => m.id === id)); setShowDelete(true); };

  const handleSave = async (formData) => {
    try {
      const payload = {
        nombre: formData.nombre,
        fecha_nacimiento: formData.fecha_nacimiento,
        sexo: formData.sexo !== undefined && formData.sexo !== '' ? Number(formData.sexo) : null,
        direccion: formData.direccion,
        descripcion: formData.descripcion,
        persona1: formData.persona1,
        persona1tel: formData.persona1tel,
        persona1ig: formData.persona1ig,
        persona2: formData.persona2,
        persona2tel: formData.persona2tel,
        persona2ig: formData.persona2ig,
        mensajeRescate: formData.mensajeRescate,
        id_usuario: formData.id_usuario ? Number(formData.id_usuario) : null,
        codigo_id: formData.codigo_id ? Number(formData.codigo_id) : null,
      };

      let mascotaId = selected?.id;

      if (selected) {
        await api.updateMascota(selected.id, payload);
      } else {
        const res = await api.createMascota(payload);
        mascotaId = res.data?.id || res.data?.data?.id;
        if (!mascotaId) throw new Error('No se obtuvo el ID de la mascota creada');
      }

      if (imageFile) {
        await api.uploadMascotaFoto(mascotaId, imageFile, payload);
      }

      setShowEdit(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteMascota(selected.id);
      setShowDelete(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Mascotas</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nueva
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Badge variant="primary">Total: {stats.total}</Badge>
        <Badge variant="info">♂ Machos: {stats.machos}</Badge>
        <Badge variant="warning">♀ Hembras: {stats.hembras}</Badge>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Buscar por nombre, ID, dueño o código..." />
        </div>
        <SexoFilter value={sexoFiltro} onChange={setSexoFiltro} />
      </div>

      <CardGrid
        items={filtradas}
        loading={loading}
        error={error}
        emptyMessage="No se encontraron mascotas."
        columns="repeat(auto-fill, minmax(260px, 1fr))"
        renderCard={(m) => (
          <div className="sp-card" style={{ padding: 0, overflow: 'hidden' }}>
            <img
              src={getImageUrl(m.urlImg)}
              alt={m.nombre}
              style={{ width: '100%', height: 160, objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/smartpet-default.jpg'; }}
            />
            <div style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>{m.nombre}</h3>
                <span style={{ fontSize: '1.1rem' }}>{m.sexo === 1 ? '♀' : m.sexo === 0 ? '♂' : '⚥'}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.55rem 0' }}>
                <Badge icon={Cake}>{calcularEdad(m.fecha_nacimiento)}</Badge>
                {m.direccion && <Badge icon={MapPin}>{m.direccion}</Badge>}
              </div>
              <p style={{ fontSize: '0.82rem', color: 'var(--sp-muted)', margin: '0.4rem 0 0.9rem', minHeight: '2.2em' }}>
                {m.descripcion || 'Sin descripción'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--sp-muted)', marginBottom: '0.75rem' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <User size={13} /> {usuariosMap[String(m.id_usuario)] || `ID ${m.id_usuario ?? '—'}`}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <QrCode size={13} /> {codigosMap[String(m.codigo_id)] || (m.codigo_id ? `ID ${m.codigo_id}` : 'Sin código')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button className="sp-btn sp-btn--light" style={{ flex: 1, minHeight: 36, fontSize: '0.82rem' }} onClick={() => handleEdit(m)}>
                  Editar
                </button>
                <button className="sp-btn sp-btn--danger" style={{ flex: 1, minHeight: 36, fontSize: '0.82rem' }} onClick={() => handleDelete(m.id)}>
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}
      />

      {showEdit && (
        <EditModal
          title={selected ? 'Editar mascota' : 'Crear mascota'}
          fields={[
            {
              name: 'imagen',
              label: 'Foto',
              type: 'image',
              size: 150,
              onChange: (e) => setImageFile(e.target.files[0]),
            },
            { name: 'nombre', label: 'Nombre' },
            {
              name: 'sexo',
              label: 'Sexo',
              type: 'radio',
              options: [
                { value: 0, label: 'Macho', icon: '♂' },
                { value: 1, label: 'Hembra', icon: '♀' },
              ],
            },
            { name: 'fecha_nacimiento', label: 'Fecha de nacimiento', type: 'date' },
            { name: 'direccion', label: 'Dirección' },
            { name: 'descripcion', label: 'Descripción', type: 'textarea' },
            { name: 'persona1', label: 'Contacto 1 (nombre)' },
            { name: 'persona1tel', label: 'Tel. contacto 1' },
            { name: 'persona1ig', label: 'IG contacto 1' },
            { name: 'persona2', label: 'Contacto 2 (nombre)' },
            { name: 'persona2tel', label: 'Tel. contacto 2' },
            { name: 'persona2ig', label: 'IG contacto 2' },
            { name: 'mensajeRescate', label: 'Mensaje de rescate', type: 'textarea' },
          ]}
          initialValues={selected || {}}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar a ${selected.nombre}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default MascotasPanel;